<template>
  <div class="flow-page">
    <Card>
      <template #title>
        <div class="title-row">
          <span class="title-main">车票改签</span>
          <Tag v-if="state.fromOrderSn" color="green">已载入待改签车票</Tag>
          <Tag v-else color="default">未指定订单</Tag>
        </div>
      </template>
      <Steps :current="state.currentStep">
        <Step title="选择车票" />
        <Step title="变更车次" />
        <Step title="选择乘车人" />
        <Step title="核对信息" />
      </Steps>
    </Card>

    <Card v-if="state.currentStep === 0" title="请选择需改签的车票">
      <Table
        :columns="originColumns"
        :data-source="state.originOrderList"
        :pagination="false"
        row-key="orderSn"
      >
        <template #action="{ record }">
          <Button type="link" @click="pickOrigin(record)">变更</Button>
        </template>
      </Table>
    </Card>

    <Card v-if="state.currentStep === 1" title="变更乘车日期及车次">
      <Alert
        type="info"
        show-icon
        message="按铁路旅客运输有关规定办理改签时，发到站一般保持不变。请先选择乘车日期，点击「查询」后系统将展示有余票的列车；具体可否改签、是否核收改签费及票款差额，以系统校验与支付确认时为准。"
      />
      <div class="origin-box">
        原票：{{ state.originOrder?.trainNumber }} 次，乘车日期
        <strong>{{ state.originOrder?.ridingDate }}</strong>，{{ state.originOrder?.departure }} 至
        {{ state.originOrder?.arrival }}
      </div>
      <Space wrap align="center" class="reschedule-query-bar">
        <span class="query-label">改签后乘车日期</span>
        <DatePicker
          v-model:value="state.rescheduleDate"
          :allow-clear="false"
          :disabled-date="disabledRescheduleDate"
          format="YYYY-MM-DD"
        />
        <Button type="primary" :loading="state.candidateLoading" @click="loadCandidateTrains">
          查询
        </Button>
      </Space>
      <Spin :spinning="state.candidateLoading">
        <div v-if="!state.candidateTrainList.length && !state.candidateLoading" class="empty-hint">
          暂无查询结果，请调整乘车日期后重新查询。
        </div>
        <RadioGroup v-else v-model:value="state.newTrainId" class="train-radio-list">
          <Radio
            v-for="item in state.candidateTrainList"
            :key="item.trainId"
            :value="item.trainId"
          >
            {{ item.trainNumber }} {{ item.departureTime }}-{{ item.arrivalTime }}
            <template v-if="item.duration">（历时 {{ item.duration }}）</template>
            ，公布票价（元）￥{{ item.price }}（具体票价以确认支付时为准）
          </Radio>
        </RadioGroup>
      </Spin>

      <div class="action-row">
        <Button @click="state.currentStep = 0">返回</Button>
        <Button type="primary" :disabled="!state.newTrainId" @click="state.currentStep = 2">
          提交
        </Button>
      </div>
    </Card>

    <Card v-if="state.currentStep === 2" title="选择乘车人">
      <Alert
        type="info"
        show-icon
        message="请选择本次办理改签的乘车人；可办理整单改签，也可仅为部分乘车人办理改签（以系统支持为准）。"
      />
      <div class="origin-box">
        订单号：{{ state.originOrder?.orderSn }}　原车次：{{ state.originOrder?.trainNumber }} 次
      </div>
      <CheckboxGroup v-model:value="state.selectedPassengerIds" class="train-radio-list">
        <Checkbox
          v-for="item in state.originOrder?.passengers || []"
          :key="item.id"
          :value="item.id"
        >
          {{ item.realName }}（{{ item.ticketType }}）
        </Checkbox>
      </CheckboxGroup>
      <div class="action-row">
        <Button @click="state.currentStep = 1">返回</Button>
        <Button
          type="primary"
          :disabled="!state.selectedPassengerIds.length"
          :loading="state.rescheduleFeeLoading"
          @click="goToConfirmStep"
        >
          提交
        </Button>
      </div>
    </Card>

    <Card v-if="state.currentStep === 3" title="核对车票改签信息">
      <Descriptions bordered :column="1" size="small">
        <DescriptionsItem label="原列车">{{ state.originOrder?.trainNumber }} 次</DescriptionsItem>
        <DescriptionsItem label="改签后乘车日期">{{ rescheduleDateText }}</DescriptionsItem>
        <DescriptionsItem label="改签后列车">{{ targetTrain?.trainNumber }} 次</DescriptionsItem>
        <DescriptionsItem label="办理人数">{{ state.selectedPassengerIds.length }} 人</DescriptionsItem>
        <DescriptionsItem label="原票票价（元/人）">￥{{ state.originOrder?.amount }}</DescriptionsItem>
        <DescriptionsItem label="新票票价（元/人）">￥{{ targetTrain?.price }}</DescriptionsItem>
        <DescriptionsItem label="票款差额（元/人）">
          <span :class="diffAmount >= 0 ? 'up' : 'down'">
            {{ diffAmount >= 0 ? '应补票款' : '应退票款' }} ￥{{ Math.abs(diffAmount).toFixed(2) }}
          </span>
        </DescriptionsItem>
      </Descriptions>

      <Spin :spinning="state.rescheduleFeeLoading">
        <RefundRescheduleFeePanel
          v-if="state.rescheduleFeeData?.available"
          mode="reschedule"
          title="改签费用"
          compact
          :reschedule-fee-yuan="Number(state.rescheduleFeeData.rescheduleFeeYuan)"
          :reschedule-service-fee-yuan="Number(state.rescheduleFeeData.serviceFeeYuan)"
          :price-diff-yuan="Number(state.rescheduleFeeData.priceDiffYuan)"
          :total-pay-yuan="Number(state.rescheduleFeeData.totalPayYuan)"
          @show-rules="state.rulesModalVisible = true"
        />
        <div
          v-else-if="!state.rescheduleFeeLoading && state.rescheduleFeeTried"
          class="fee-unavailable"
        >
          {{ state.rescheduleFeeData?.message || '暂无法获取费用，具体费用请联系客服' }}
        </div>
      </Spin>

      <div class="action-row">
        <Button @click="state.currentStep = 2">返回</Button>
        <Button type="primary" @click="submitReschedule">确认改签</Button>
      </div>
    </Card>
    <RefundRescheduleRulesModal v-model:visible="state.rulesModalVisible" />
  </div>
</template>

<script setup>
import {
  Card,
  Steps,
  Step,
  Tag,
  Table,
  Button,
  Alert,
  RadioGroup,
  Radio,
  CheckboxGroup,
  Checkbox,
  Descriptions,
  DescriptionsItem,
  message,
  DatePicker,
  Space,
  Spin
} from 'ant-design-vue'
import { computed, reactive, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import dayjs from 'dayjs'
import { fetchOrderBySn, fetchTicketSearch, fetchRescheduleFeeEstimate } from '@/service'
import { TICKET_TYPE_LIST } from '@/constants'
import RefundRescheduleFeePanel from '@/components/refund-reschedule/RefundRescheduleFeePanel.vue'
import RefundRescheduleRulesModal from '@/components/refund-reschedule/RefundRescheduleRulesModal.vue'
import Cookie from 'js-cookie'

const route = useRoute()

/** 查询失败时的演示车次（离线仍可走完流程） */
const DEMO_FALLBACK_TRAINS = [
  {
    trainId: 'demo-1',
    trainNumber: 'G37',
    departureTime: '10:05',
    arrivalTime: '10:42',
    duration: '37分',
    price: 62.0
  },
  {
    trainId: 'demo-2',
    trainNumber: 'G41',
    departureTime: '11:20',
    arrivalTime: '11:58',
    duration: '38分',
    price: 55.0
  }
]

const state = reactive({
  currentStep: 0,
  /** 选中的新车次 trainId（与余票查询结果一致） */
  newTrainId: null,
  originOrder: null,
  selectedPassengerIds: [],
  fromOrderSn: null,
  /** 改签目标日期，可改为原乘车日之后的任意可售日期 */
  rescheduleDate: null,
  candidateLoading: false,
  originOrderList: [
    {
      orderSn: 'CG202604300001',
      trainNumber: 'G33',
      departure: '北京南',
      arrival: '天津南',
      ridingDate: '2026-05-05',
      amount: 58.0,
      passengers: [
        { id: 1, realName: '张三', ticketType: '成人票', seatType: 2 },
        { id: 2, realName: '李四', ticketType: '学生票', seatType: 2 }
      ]
    },
    {
      orderSn: 'CG202604300002',
      trainNumber: 'G115',
      departure: '南京南',
      arrival: '上海虹桥',
      ridingDate: '2026-05-06',
      amount: 139.5,
      passengers: [
        { id: 3, realName: '王五', ticketType: '成人票', seatType: 2 },
        { id: 4, realName: '赵六', ticketType: '成人票', seatType: 2 }
      ]
    }
  ],
  candidateTrainList: [],
  rulesModalVisible: false,
  rescheduleFeeLoading: false,
  rescheduleFeeTried: false,
  rescheduleFeeData: null,
  quoteTimestamp: null
})

const mapApiToOrigin = (data) => {
  const list = data.passengerDetails || []
  const firstAmt = list[0]?.amount != null ? list[0].amount / 100 : 0
  return {
    orderSn: data.orderSn,
    trainNumber: data.trainNumber,
    departure: data.departure,
    arrival: data.arrival,
    ridingDate: data.ridingDate,
    amount: firstAmt,
    passengers: list.map((p) => ({
      id: p.id,
      realName: p.realName,
      seatType: p.seatType,
      ticketType:
        TICKET_TYPE_LIST.find((t) => t.value === p.ticketType)?.label ?? '成人票'
    }))
  }
}

const originColumns = [
  { title: '订单号', dataIndex: 'orderSn' },
  { title: '原车次', dataIndex: 'trainNumber' },
  { title: '日期', dataIndex: 'ridingDate' },
  { title: '区间', customRender: ({ record }) => `${record.departure} -> ${record.arrival}` },
  { title: '票价', dataIndex: 'amount', customRender: ({ text }) => `￥${text}` },
  { title: '操作', key: 'action', slots: { customRender: 'action' } }
]

const minSeatPrice = (tr) => {
  const arr = tr?.seatClassList?.map((s) => s.price).filter((x) => x != null) ?? []
  return arr.length ? Math.min(...arr) : null
}

const mapApiTrainRow = (item) => ({
  trainId: item.trainId,
  trainNumber: item.trainNumber,
  departureTime: item.departureTime,
  arrivalTime: item.arrivalTime,
  duration: item.duration,
  price: minSeatPrice(item) ?? 0,
  seatClassList: item.seatClassList || []
})

/** 与车票查询页一致：可预售日期内 */
const disabledRescheduleDate = (current) =>
  !current ||
  current < dayjs().startOf('day') ||
  current > dayjs().add(14, 'day')

const targetTrain = computed(() =>
  state.candidateTrainList.find((item) => item.trainId === state.newTrainId)
)

const rescheduleDateText = computed(() => {
  const d = state.rescheduleDate
  return d ? dayjs(d).format('YYYY-MM-DD') : '-'
})
const diffAmount = computed(() => {
  if (!state.originOrder || !targetTrain.value) return 0
  const singleDiff = targetTrain.value.price - state.originOrder.amount
  return singleDiff * (state.selectedPassengerIds.length || 1)
})

const buildRescheduleApiParams = () => {
  const o = state.originOrder
  const train = targetTrain.value
  if (!o || !train || !state.selectedPassengerIds.length) return null
  const selected = (o.passengers || []).filter((p) =>
    state.selectedPassengerIds.includes(p.id)
  )
  const seatType = selected[0]?.seatType ?? 2
  const seatInfo = train.seatClassList?.find((s) => s.type === seatType)
  return {
    orderSn: o.orderSn,
    trainId: train.trainId,
    passengers: selected.map((p) => ({
      passengerId: String(p.id),
      seatType: p.seatType ?? 2
    })),
    chooseSeats: [],
    departure: o.departure,
    arrival: o.arrival,
    ticketDate: rescheduleDateText.value,
    username: Cookie.get('username'),
    userId: Cookie.get('userId'),
    quoteSnapshot: {
      timestamp: state.quoteTimestamp || null,
      deliveryId: seatInfo?.deliveryId || null
    }
  }
}

const loadRescheduleFeeEstimate = () => {
  const params = buildRescheduleApiParams()
  if (!params) return
  state.rescheduleFeeLoading = true
  state.rescheduleFeeTried = false
  state.rescheduleFeeData = null
  fetchRescheduleFeeEstimate(params)
    .then((res) => {
      state.rescheduleFeeData = res?.data ?? null
    })
    .catch(() => {
      state.rescheduleFeeData = {
        available: false,
        message: '暂无法获取费用，具体费用请联系客服'
      }
    })
    .finally(() => {
      state.rescheduleFeeLoading = false
      state.rescheduleFeeTried = true
    })
}

const goToConfirmStep = () => {
  state.currentStep = 3
  loadRescheduleFeeEstimate()
}

const loadCandidateTrains = () => {
  if (!state.originOrder) return
  const raw = state.rescheduleDate
  if (!raw) {
    message.warning('请先选择改签后的乘车日期')
    return
  }
  const d = dayjs(raw)
  if (!d.isValid()) {
    message.warning('乘车日期无效，请重新选择')
    return
  }
  const o = state.originOrder
  state.candidateLoading = true
  fetchTicketSearch({
    fromStation: o.departure,
    toStation: o.arrival,
    departure: o.departure,
    arrival: o.arrival,
    departureDate: d.format('YYYY-MM-DD')
  })
    .then((res) => {
      if (!res.success) {
        message.error(res.message || '查询车次失败')
        state.candidateTrainList = [...DEMO_FALLBACK_TRAINS]
        state.newTrainId = null
        message.warning('余票查询暂不可用，以下为演示车次，仅供界面联调。')
        return
      }
      const list = res.data?.trainList ?? []
      state.quoteTimestamp = res.data?.timestamp ?? null
      const mapped = list.map(mapApiTrainRow)
      state.candidateTrainList = mapped
      state.newTrainId = null
      if (!mapped.length) {
        message.info('该日期暂无查询结果，可更换日期后再查。')
      }
    })
    .catch(() => {
      message.error('余票查询请求异常')
      state.candidateTrainList = [...DEMO_FALLBACK_TRAINS]
      state.newTrainId = null
      message.warning('余票查询暂不可用，以下为演示车次，仅供界面联调。')
    })
    .finally(() => {
      state.candidateLoading = false
    })
}

const pickOrigin = (record) => {
  state.originOrder = record
  state.newTrainId = null
  state.selectedPassengerIds = []
  state.rescheduleDate = record.ridingDate ? dayjs(record.ridingDate) : dayjs()
  state.currentStep = 1
  loadCandidateTrains()
}

const submitReschedule = () => {
  message.success('车票改签已提交。如有票款差额，请在订单支付时限内完成网上支付。')
  state.currentStep = 0
  state.originOrder = null
  state.newTrainId = null
  state.selectedPassengerIds = []
  state.fromOrderSn = null
}

onMounted(() => {
  const sn = route.query.orderSn
  if (!sn) return
  state.fromOrderSn = sn
  fetchOrderBySn({ orderSn: sn }).then((res) => {
    if (res.success && res.data) {
      const mapped = mapApiToOrigin(res.data)
      state.originOrderList = [mapped]
      pickOrigin(mapped)
    }
  })
})
</script>

<style lang="scss" scoped>
.flow-page {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.title-main {
  font-weight: 600;
}

.origin-box {
  margin: 12px 0;
  padding: 10px 12px;
  border: 1px solid #e6edf8;
  border-radius: 10px;
  background: #f8fbff;
}

.reschedule-query-bar {
  margin: 12px 0;
}

.query-label {
  color: #475569;
  font-weight: 500;
}

.empty-hint {
  padding: 16px 0;
  color: #64748b;
}

.train-radio-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.action-row {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.fee-unavailable {
  margin-top: 12px;
  padding: 12px 16px;
  border-radius: 4px;
  background: #f7f8fa;
  color: #999;
  font-size: 13px;
  text-align: center;
}

.up {
  color: #ea580c;
  font-weight: 700;
}

.down {
  color: #16a34a;
  font-weight: 700;
}
</style>
